'use client';

import { c as _c } from "react/compiler-runtime";
import { m } from 'framer-motion';
import { startTransition } from 'react';
import { usePopover } from 'minimal-shared/hooks';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { newSearchParams } from 'src/routes/paths';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import { useResponsive } from 'src/hooks/use-responsive';
import { fCurrencySymbol } from 'src/utils/format-number';
import { useTranslate } from 'src/locales';
import { Iconify } from 'src/components/iconify';
import { FlagIcon } from 'src/components/flag-icon';
import { useSettingsContext } from 'src/components/settings';
import { CustomPopover } from 'src/components/custom-popover';
import { CountrySelect } from 'src/components/country-select';
import { LanguageSelect } from 'src/components/language-select';
import { varTap, varHover, transitionTap } from 'src/components/animate';

// ----------------------------------------------------------------------

export function LanguagePopover(t0) {
  const $ = _c(86);
  let other;
  let sx;
  if ($[0] !== t0) {
    ({
      sx,
      ...other
    } = t0);
    $[0] = t0;
    $[1] = other;
    $[2] = sx;
  } else {
    other = $[1];
    sx = $[2];
  }
  const router = useRouter();
  const settings = useSettingsContext();
  const mdUp = useResponsive("up", "md");
  const searchParams = useSearchParams();
  const {
    t,
    currentLang,
    onChangeLang
  } = useTranslate();
  const {
    open,
    anchorEl,
    onClose,
    onOpen
  } = usePopover();
  let t1;
  if ($[3] !== onChangeLang || $[4] !== onClose) {
    t1 = newLang => {
      if (newLang) {
        onClose();
        onChangeLang(newLang);
      }
    };
    $[3] = onChangeLang;
    $[4] = onClose;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleChangeLang = t1;
  let t2;
  if ($[6] !== onClose || $[7] !== router || $[8] !== searchParams || $[9] !== settings) {
    t2 = countryName => {
      if (countryName) {
        onClose();
        startTransition(() => {
          settings.setState({
            countryName
          });
          const search = newSearchParams({
            ...Object.fromEntries(searchParams),
            country: countryName
          });
          router.push(search, {
            scroll: false
          });
        });
      }
    };
    $[6] = onClose;
    $[7] = router;
    $[8] = searchParams;
    $[9] = settings;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const handleChangeCountry = t2;
  let t3;
  let t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = varTap(0.96);
    t4 = varHover(1.04);
    t5 = transitionTap();
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
  }
  let t6;
  if ($[14] !== open) {
    t6 = theme => ({
      p: 0,
      width: 40,
      height: 40,
      ...(open && {
        backgroundColor: theme.vars.palette.action.selected
      })
    });
    $[14] = open;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== sx) {
    t7 = Array.isArray(sx) ? sx : [sx];
    $[16] = sx;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = [t6, ...t7];
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== currentLang.countryCode) {
    t9 = <FlagIcon code={currentLang.countryCode} />;
    $[21] = currentLang.countryCode;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== onOpen || $[24] !== other || $[25] !== t8 || $[26] !== t9) {
    t10 = <IconButton onClick={onOpen} component={m.button} whileTap={t3} whileHover={t4} transition={t5} aria-label="Languages button" sx={t8} {...other}>{t9}</IconButton>;
    $[23] = onOpen;
    $[24] = other;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const t11 = `top-${mdUp ? "right" : "left"}`;
  let t12;
  if ($[28] !== t11) {
    t12 = {
      placement: t11
    };
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      className: "stack",
      sx: {
        px: 2,
        pt: 4,
        pb: 3,
        gap: 2.5,
        width: 250
      }
    };
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t12) {
    t14 = {
      arrow: t12,
      paper: t13
    };
    $[31] = t12;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t) {
    t15 = t("Country");
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t("Choose a country");
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== handleChangeCountry) {
    t17 = (event, newValue) => handleChangeCountry(newValue);
    $[37] = handleChangeCountry;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== currentLang.countryName || $[40] !== t15 || $[41] !== t16 || $[42] !== t17) {
    t18 = <CountrySelect hidePhoneCode={true} id="langPopover" label={t15} place={t16} value={currentLang.countryName} onChange={t17} />;
    $[39] = currentLang.countryName;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      typography: "caption",
      color: "text.secondary"
    };
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Iconify width={13} icon="solar:info-circle-line-duotone" sx={{
      mr: 0.5
    }} />;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  const t21 = `${t("If you change countries, item availability, prices, shipping costs, and taxes may change (including items in your cart)")}.`;
  let t22;
  if ($[46] !== t21) {
    t22 = <Box component="span" sx={t19}>{t20}{t21}</Box>;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t) {
    t23 = t("Language");
    $[48] = t;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  const t24 = currentLang.name;
  let t25;
  if ($[50] !== t) {
    t25 = t("Choose a language");
    $[50] = t;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== handleChangeLang) {
    t26 = (event_0, newValue_0) => handleChangeLang(newValue_0);
    $[52] = handleChangeLang;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== currentLang.name || $[55] !== t23 || $[56] !== t25 || $[57] !== t26) {
    t27 = <LanguageSelect id="langPopover" label={t23} value={t24} place={t25} onChange={t26} />;
    $[54] = currentLang.name;
    $[55] = t23;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      gap: 0.5
    };
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      typography: "caption",
      color: "text.secondary"
    };
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== t) {
    t30 = t("Currency");
    $[61] = t;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== t30) {
    t31 = <Box sx={t29}>{t30}</Box>;
    $[63] = t30;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      typography: "body2"
    };
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  const t33 = currentLang.numberFormat?.currency;
  let t34;
  if ($[66] !== currentLang.numberFormat) {
    t34 = fCurrencySymbol(currentLang.numberFormat);
    $[66] = currentLang.numberFormat;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] !== t33 || $[69] !== t34) {
    t35 = <Box sx={t32}>{t33} {t34}</Box>;
    $[68] = t33;
    $[69] = t34;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  let t36;
  if ($[71] !== t31 || $[72] !== t35) {
    t36 = <Box className="stack" sx={t28}>{t31}{t35}</Box>;
    $[71] = t31;
    $[72] = t35;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] !== anchorEl || $[75] !== onClose || $[76] !== open || $[77] !== t14 || $[78] !== t18 || $[79] !== t22 || $[80] !== t27 || $[81] !== t36) {
    t37 = <CustomPopover open={open} onClose={onClose} anchorEl={anchorEl} slotProps={t14}>{t18}{t22}{t27}{t36}</CustomPopover>;
    $[74] = anchorEl;
    $[75] = onClose;
    $[76] = open;
    $[77] = t14;
    $[78] = t18;
    $[79] = t22;
    $[80] = t27;
    $[81] = t36;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  let t38;
  if ($[83] !== t10 || $[84] !== t37) {
    t38 = <>{t10}{t37}</>;
    $[83] = t10;
    $[84] = t37;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  return t38;
}